import { emptyPaginatedData } from "./helper";
import api, { queryClient } from "@app/config/api/api";
import type { operations } from "@generated/openapi";
import type { DatasetId } from "@globals";
import { type DatasetExportFormats } from "@lib/dataset/DatasetExportFormats";
import { notifications } from "@lib/notification/notification";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";

export const useDatasetList = (query: operations["list_datasets"]["parameters"]["query"] = {}) =>
  api.useSuspenseQuery(
    "get",
    "/datasets/",
    { params: { query } },
    {
      initialData: emptyPaginatedData
    }
  );

export const useDatasetListWithSorting = (query: operations["list_datasets"]["parameters"]["query"] = {}) =>
  api.useSuspenseQuery("get", "/datasets/", { params: { query } });

export const usePostDataset = () => api.useMutation("post", "/datasets/");

export const datasetDetailsOptions = (dataset_id: DatasetId) =>
  api.queryOptions("get", `/datasets/{dataset_id}`, {
    params: { path: { dataset_id } },
    queryKey: ["datasets", dataset_id]
  });
export const useDatasetDetails = (dataset_id: DatasetId) => useQuery(datasetDetailsOptions(dataset_id));
export const useDatasetDetailsSuspense = (dataset_id: DatasetId) => useSuspenseQuery(datasetDetailsOptions(dataset_id));

export const useDeleteDataset = () =>
  api.useMutation("delete", "/datasets/{dataset_id}", {
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: api.queryOptions("get", "/datasets/").queryKey });
      notifications.success("Dataset deleted successfully");
    },
    onError(error) {
      notifications.error(`Failed to delete dataset: ${error.message}`);
    }
  });

export const usePostParseDataset = () => api.useMutation("post", "/datasets/parse/csv/");

export const usePatchDatapoint = () => api.useMutation("patch", "/datasets/datapoints/{datapoint_id}");

export const usePatchDatapoints = () => api.useMutation("post", "/datasets/datapoints/");

export const datasetSpaceOptions = (dataset_id: DatasetId) =>
  api.queryOptions("get", `/datasets/{dataset_id}/space`, {
    params: { path: { dataset_id } },
    queryKey: ["datasets", dataset_id, "space"]
  });

export const useDatasetSpace = (dataset_id?: DatasetId) =>
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  useQuery({ ...datasetSpaceOptions(dataset_id!), enabled: Boolean(dataset_id) });

export const getDatasetSpaceDetails = async (dataset_id: DatasetId) =>
  queryClient.fetchQuery(
    api.queryOptions("get", `/datasets/{dataset_id}/space`, {
      params: { path: { dataset_id } }
    })
  );

export type ListDatapointsQuery = operations["list_datapoints"]["parameters"]["query"];

export const useDatapointsList = (query: ListDatapointsQuery, dataset_id: DatasetId) =>
  api.useQuery("get", "/datasets/{dataset_id}/datapoints/", {
    params: { query, path: { dataset_id } }
  });

export const exportDatasetToFile = async (dataset_id: DatasetId, format: DatasetExportFormats) =>
  queryClient.fetchQuery(
    api.queryOptions(
      "get",
      `/datasets/{dataset_id}:export`,
      {
        params: { path: { dataset_id }, query: { format } },
        parseAs: "blob"
      },
      {
        enabled: false
      }
    )
  );

export const invalidateDatasetSpace = async (dataset_id: DatasetId) =>
  queryClient.invalidateQueries({ queryKey: datasetSpaceOptions(dataset_id).queryKey });
